import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  resumoQuarto: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  precoDestaque: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    fontSize: "1.15rem",
  },
  secaoTitulo: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  confirmacaoBox: {
    backgroundColor: "#e8f5e9",
    padding: theme.spacing(2),
    borderRadius: 8,
    textAlign: "center",
  },
}));

const today = new Date().toISOString().split("T")[0];
const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

const HotelReservaModal = ({ open, onClose, quarto, contact, onReservaCriada }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [reservaCriada, setReservaCriada] = useState(null);
  const [buscandoHospede, setBuscandoHospede] = useState(false);

  // Campos usados quando não há quarto pré-selecionado
  const [entrada, setEntrada] = useState(today);
  const [saida, setSaida] = useState(tomorrow);
  const [quartoId, setQuartoId] = useState("");

  const [form, setForm] = useState({
    nome: "",
    cpf: "",
    telefone: "",
    email: "",
    cidade: "",
    estado: "",
    numHospedes: 1,
    observacoes: "",
    hospede_id: null,
  });

  useEffect(() => {
    if (open && contact) {
      const telefone = contact.number ? contact.number.replace(/\D/g, "") : "";
      const nome = contact.name || "";
      setForm((prev) => ({ ...prev, nome, telefone }));

      if (telefone) {
        setBuscandoHospede(true);
        api
          .get("/hotel-mineiro/hospede", { params: { telefone } })
          .then(({ data }) => {
            if (data?.data) {
              const h = data.data;
              setForm((prev) => ({
                ...prev,
                nome: h.nome || prev.nome,
                cpf: h.cpf || "",
                telefone: h.telefone || prev.telefone,
                email: h.email || "",
                cidade: h.cidade || "",
                estado: h.estado || "",
                hospede_id: h.id,
              }));
              toast.info(`Hóspede encontrado: ${h.nome}. Dados preenchidos automaticamente.`);
            }
          })
          .catch(() => {})
          .finally(() => setBuscandoHospede(false));
      }
    }
  }, [open, contact]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
      hospede_id:
        field === "cpf" || field === "telefone" ? null : prev.hospede_id,
    }));
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome do hóspede é obrigatório.");
      return;
    }

    const quartoIdFinal = quarto ? quarto.id : Number(quartoId);
    if (!quartoIdFinal) {
      toast.error("Informe o ID do quarto.");
      return;
    }

    const entradaFinal = quarto ? quarto.entrada : entrada;
    const saidaFinal = quarto ? quarto.saida : saida;

    if (!entradaFinal || !saidaFinal) {
      toast.error("Informe as datas de entrada e saída.");
      return;
    }
    if (saidaFinal <= entradaFinal) {
      toast.error("A data de saída deve ser posterior à entrada.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        quarto_id: quartoIdFinal,
        entrada: entradaFinal,
        saida: saidaFinal,
        nome: form.nome,
        num_hospedes: Number(form.numHospedes) || 1,
      };

      if (form.observacoes) payload.observacoes = form.observacoes;
      if (form.hospede_id) payload.hospede_id = form.hospede_id;
      if (form.cpf) payload.cpf = form.cpf;
      if (form.telefone) payload.telefone = form.telefone;
      if (form.email) payload.email = form.email;
      if (form.cidade) payload.cidade = form.cidade;
      if (form.estado) payload.estado = form.estado;

      const { data } = await api.post("/hotel-mineiro/reserva", payload);
      setReservaCriada(data.data);
      toast.success("Reserva criada com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReservaCriada(null);
    setEntrada(today);
    setSaida(tomorrow);
    setQuartoId("");
    setForm({
      nome: "",
      cpf: "",
      telefone: "",
      email: "",
      cidade: "",
      estado: "",
      numHospedes: 1,
      observacoes: "",
      hospede_id: null,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {reservaCriada
          ? "Reserva Confirmada!"
          : "Realizar Reserva — Hotel do Mineiro"}
      </DialogTitle>
      <DialogContent>
        {reservaCriada ? (
          <div className={classes.confirmacaoBox}>
            <Typography variant="h6" gutterBottom>
              ✅ {reservaCriada.mensagem}
            </Typography>
            <Typography variant="body1">
              <strong>Reserva:</strong> #{reservaCriada.reserva_id}
            </Typography>
            <Typography variant="body1">
              <strong>Hóspede:</strong> {reservaCriada.hospede?.nome}
            </Typography>
            <Typography variant="body1">
              <strong>Quarto:</strong> {reservaCriada.quarto}
            </Typography>
            <Typography variant="body1">
              <strong>Check-in:</strong> {reservaCriada.entrada}
            </Typography>
            <Typography variant="body1">
              <strong>Check-out:</strong> {reservaCriada.saida}
            </Typography>
            <Typography variant="body1">
              <strong>Noites:</strong> {reservaCriada.noites}
            </Typography>
            <Typography variant="body1" className={classes.precoDestaque}>
              <strong>Total:</strong> {reservaCriada.valor_total}
            </Typography>
          </div>
        ) : (
          <>
            {quarto ? (
              <Paper className={classes.resumoQuarto} elevation={0}>
                <Typography variant="subtitle2">
                  Quarto {quarto.numero} — {quarto.tipo} · {quarto.capacidade}{" "}
                  pessoa(s)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {quarto.entrada} → {quarto.saida} · {quarto.noites} noite(s)
                </Typography>
                <Typography className={classes.precoDestaque}>
                  Total: R$ {Number(quarto.total_estadia).toFixed(2)}
                </Typography>
              </Paper>
            ) : (
              <>
                <Typography className={classes.secaoTitulo} variant="subtitle2">
                  Período e Quarto
                </Typography>
                <Divider style={{ marginBottom: 12 }} />
                <Grid container spacing={2} style={{ marginBottom: 8 }}>
                  <Grid item xs={4}>
                    <TextField
                      label="Check-in *"
                      type="date"
                      value={entrada}
                      onChange={(e) => setEntrada(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Check-out *"
                      type="date"
                      value={saida}
                      onChange={(e) => setSaida(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="ID do Quarto *"
                      value={quartoId}
                      onChange={(e) => setQuartoId(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="Ex: 2"
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {buscandoHospede && (
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption">
                  Buscando hóspede cadastrado...
                </Typography>
              </div>
            )}

            <Typography className={classes.secaoTitulo} variant="subtitle2">
              Dados do Hóspede
            </Typography>
            <Divider style={{ marginBottom: 12 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Nome completo *"
                  value={form.nome}
                  onChange={handleChange("nome")}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="CPF"
                  value={form.cpf}
                  onChange={handleChange("cpf")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="000.000.000-00"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Telefone"
                  value={form.telefone}
                  onChange={handleChange("telefone")}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="E-mail"
                  value={form.email}
                  onChange={handleChange("email")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Cidade"
                  value={form.cidade}
                  onChange={handleChange("cidade")}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="UF"
                  value={form.estado}
                  onChange={handleChange("estado")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 2 }}
                  placeholder="SP"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Nº Hóspedes"
                  value={form.numHospedes}
                  onChange={handleChange("numHospedes")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: quarto ? quarto.capacidade : 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observações / pedidos especiais"
                  value={form.observacoes}
                  onChange={handleChange("observacoes")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {reservaCriada ? (
          <Button onClick={handleClose} color="primary" variant="contained">
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={18} /> : "Confirmar Reserva"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HotelReservaModal;
